import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { Flex } from "antd"
import { AVAILABILITY_STATUSES } from "../constants"
import { formatTicketedSlot, isTicketedListing, summariseSlots } from "../utils"
import ReasonChip from "./ReasonChip"
import styles from "./AvailabilityDayCard.module.css"

const STATUS_CLASS = {
  [AVAILABILITY_STATUSES.AVAILABLE]: styles.available,
  [AVAILABILITY_STATUSES.LIMITED]: styles.limited,
  [AVAILABILITY_STATUSES.FULLY_BOOKED]: styles.fullyBooked,
  [AVAILABILITY_STATUSES.NOT_AVAILABLE]: styles.notAvailable
}

/**
 * One day in the availability grid.
 *
 * The card is always the same height; only the coloured "state" block inside it changes. A day the
 * feed does not describe renders as an empty card rather than guessing a status — the server owns
 * that call (A2), and the endpoint is not built yet, so "unknown" is the common case today.
 *
 * Hovering swaps the collapsed slot line for the reasons in full plus a View Details link, which
 * is why both are rendered and CSS decides: measuring on hover would reflow the whole grid.
 */
const AvailabilityDayCard = ({ dateKey, isCurrentMonth, day, listing, onOpenDetails }) => {
  const { t } = useTranslation()

  const dayNumber = moment(dateKey).format("D")
  const reasons = day?.reasons || []
  const isTicketed = isTicketedListing(listing)
  const { times, remainingCount } = summariseSlots(day)
  const hasSlots = (day?.slots || []).length > 0

  const slotLine = isTicketed
    ? (day?.slots || []).slice(0, times.length).map((slot) => formatTicketedSlot(slot, t))
    : [times.join(", ")]

  return (
    <div className={`${styles.card} ${isCurrentMonth ? "" : styles.outsideMonth}`}>
      <span className={styles.dayNumber}>{dayNumber}</span>

      {day && (
        <Flex vertical gap="var(--spacing-xxs)" className={`${styles.state} ${STATUS_CLASS[day.status] || ""}`}>
          {reasons.length > 0 && (
            <Flex vertical gap="var(--spacing-xxs)" align="flex-start" className={styles.reasons}>
              {reasons.map((reason, index) => (
                <ReasonChip key={`${reason.type}-${reason.blackout_id || index}`} reason={reason} />
              ))}
            </Flex>
          )}

          {hasSlots && (
            <div className={styles.slots}>
              {slotLine.map((line) => (
                <p key={line} className={styles.slotLine}>
                  {line}
                </p>
              ))}
              {remainingCount > 0 && (
                <p className={styles.slotLine}>
                  {t("availability.more_slots", { defaultValue: "+{{count}} more", count: remainingCount })}
                </p>
              )}
            </div>
          )}

          {/* Detail and the link live in the flow but stay hidden until hover, so the row never reflows. */}
          <div className={styles.hoverDetail}>
            {reasons.map((reason) =>
              reason.detail ? (
                <p key={`${reason.type}-detail`} className={styles.detailText}>
                  {reason.detail}
                </p>
              ) : null
            )}
            {day.bookings_count > 0 && (
              <p className={styles.detailText}>
                {t("availability.bookings_count", { defaultValue: "{{count}} bookings", count: day.bookings_count })}
              </p>
            )}
            <button type="button" className={styles.detailsLink} onClick={() => onOpenDetails(dateKey)}>
              {t("availability.view_details", { defaultValue: "View Details" })}
            </button>
          </div>
        </Flex>
      )}
    </div>
  )
}

AvailabilityDayCard.propTypes = {
  dateKey: PropTypes.string.isRequired,
  isCurrentMonth: PropTypes.bool,
  /** Null when the feed does not describe this day — see the note above. */
  day: PropTypes.shape({
    status: PropTypes.string,
    slots: PropTypes.array,
    slots_total: PropTypes.number,
    bookings_count: PropTypes.number,
    reasons: PropTypes.array
  }),
  listing: PropTypes.shape({ listing_type: PropTypes.string }),
  onOpenDetails: PropTypes.func.isRequired
}

export default AvailabilityDayCard
